import React, { useEffect, useState } from 'react';

import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useDispatch, useSelector } from 'react-redux';
import EditIcon from '@material-ui/icons/Edit';
import { MaterialSelectField } from 'components/FormFields';

import { KeyboardDatePicker } from '@material-ui/pickers';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { EditableTable } from 'components/UI';
import i18n from 'i18n-js';
import moment from 'moment';
import useDeepCompareEffect from 'use-deep-compare-effect';

import useStyles from './Pricing.styled';

const VehiclesGroupsPricing = ({
  currentGroup,
  step,
  loading,
  pricingRulesets,
  onSave,
  edit,
  setEdit,
  pricing,
  setPricing,
  fleet = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [columns, setColumns] = React.useState([]);
  const { localeFormat } = useSelector((state) => state.organizations);

  const onChange = (e) => {
    setPricing(e.target.value);
    if (!step?.currentRuleset || step?.currentRuleset?.rule_set?.id !== e.target.value) {
      onSave(true);
    }
  };

  const handleRuleset = (payload, ruleset, resolve, reject) => {
    dispatch(
      VehiclesGroupsActions.handleRulesetToVehiclesGroups(
        currentGroup?.id,
        ruleset,
        payload,
        resolve,
        reject
      )
    );
  };

  const deleteRuleset = (ruleset, payload, resolve, reject) => {
    dispatch(
      VehiclesGroupsActions.deleteRulesetFromVehiclesGroups(
        currentGroup?.id,
        ruleset,
        payload,
        resolve,
        reject
      )
    );
  };

  const parseTableRow = () => {
    const tableData = step?.groupRulesets?.map((val) => {
      return {
        rule_set_id: val?.rule_set?.id,
        start_date: val?.start_time ? moment(val?.start_time).format(localeFormat) : '',
        end_date: val?.end_time ? moment(val?.end_time).format(localeFormat) : '',
        rule_set: val.id,
      };
    });
    return tableData || [];
  };

  const renderTexts = () => {
    let texts = {};
    if (step?.currentRuleset) {
      texts.title = i18n.t('ruleset.current_location_title');
      texts.paragraph = fleet
        ? i18n.t('ruleset.current_location_subtitle_fleet')
        : i18n.t('ruleset.current_location_subtitle');
    } else {
      texts.title = i18n.t('ruleset.rulesel_apply_title');
      texts.paragraph = fleet
        ? i18n.t('ruleset.rulesel_apply_subtitle_fleet')
        : i18n.t('ruleset.rulesel_apply_subtitle');
    }
    return texts;
  };

  useEffect(() => {
    dispatch(VehiclesGroupsActions.fetchRelatedRulesets('pricing', { limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (currentGroup?.id) {
      dispatch(VehiclesGroupsActions.fetchVehiclesGroupRulesets(currentGroup?.id));
    }
  }, [dispatch, currentGroup]);

  useEffect(() => {
    if (step?.currentRuleset?.rule_set?.id) {
      setPricing(step?.currentRuleset?.rule_set?.id);
    }
    setEdit(false);
  }, [step]);

  useDeepCompareEffect(() => {
    if (pricingRulesets.length > 0) {
      const parsedPricings = pricingRulesets.reduce((obj, val) => {
        obj[val.id] = val.name;
        return obj;
      }, {});

      setColumns([
        {
          title: i18n.t('ruleset.title'),
          field: 'rule_set_id',
          lookup: parsedPricings,
          dataCy: 'ruleSelect',
        },
        {
          title: i18n.t('common.start_date'),
          field: 'start_date',
          editComponent: (props) => {
            return (
              <KeyboardDatePicker
                id="date-start"
                label="Start date"
                format="MM/DD/YYYY"
                className="material-datepicker"
                minDate={new Date()}
                value={props.value ? moment(new Date(props.value)) : null}
                onChange={props.onChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            );
          },
        },
        {
          title: i18n.t('common.end_date'),
          field: 'end_date',
          editComponent: (props) => {
            return (
              <KeyboardDatePicker
                id="date-end"
                label="End date"
                format="MM/DD/YYYY"
                className="material-datepicker"
                minDate={new Date()}
                value={props.value ? moment(new Date(props.value)) : null}
                onChange={props.onChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            );
          },
        },
      ]);
    }
    setEdit(false);
  }, [pricingRulesets]);

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Typography gutterBottom component="div">
              <Box data-cy="step-title" fontWeight="fontWeightMedium" fontSize={14} my={2}>
                {renderTexts().title}
              </Box>
            </Typography>
            <Typography gutterBottom component="div" fontWeight="fontWeightRegular">
              <Box fontSize={14} mb={2} className={classes.textContent}>
                {renderTexts().paragraph}
              </Box>
            </Typography>
          </Grid>
          {step?.currentRuleset && (
            <Grid item xs={12}>
              <Typography gutterBottom component="div" color="textSecondary">
                <Box fontSize={13} mb={1} className={classes.pricingTitle}>
                  {i18n.t('vehicle_groups.pricing_rule_set')}
                </Box>
              </Typography>
            </Grid>
          )}
          {(!step?.currentRuleset || edit) && (
            <Grid item xs={12}>
              <Grid container direction="row" alignItems="flex-end" spaging="2">
                <Grid item xs={5}>
                  <MaterialSelectField
                    options={pricingRulesets?.map((val) => {
                      return {
                        value: val.id,
                        label: val.name,
                      };
                    })}
                    formStyle={{ maxWidth: '100%' }}
                    field={{ name: 'pricing', value: pricing, onChange }}
                    placeholder={i18n.t('ruleset.select_pricing_ruleset')}
                    data-cy="pricingSelect"
                  />
                </Grid>
                <Grid item xs={5}>
                  <Button
                    onClick={() => {
                      let payload = { rule_set_type: 'PRICING' };
                      const type = step?.currentRuleset ? 'edit' : 'add';
                      const ruleset = type === 'edit' ? step?.currentRuleset?.id : null;
                      if (step?.currentRuleset) {
                        payload.new_rule_set_id = pricing;
                      } else {
                        payload.rule_set_id = pricing;
                      }
                      handleRuleset(payload, ruleset);
                    }}
                    disabled={loading || !pricing}
                    data-cy="save-pricing-rule"
                    color="primary"
                    size="medium"
                  >
                    {i18n.t('common.save')}
                  </Button>

                  {step?.currentRuleset ? (
                    <Button
                      onClick={() => {
                        setEdit(false);
                        onSave(false);
                        setPricing(step?.currentRuleset?.rule_set?.id);
                      }}
                      disabled={loading}
                      color="primary"
                      size="medium"
                    >
                      {i18n.t('common.cancel')}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setPricing('');
                      }}
                      disabled={!pricing}
                      data-cy="clear-rule"
                      color="primary"
                      size="medium"
                    >
                      {i18n.t('common.clear')}
                    </Button>
                  )}
                </Grid>
                <Grid item xs={8} className={classes.disclaimer}>
                  <span>{i18n.t('vehicle_groups.save_disclaimer')}</span>
                </Grid>
              </Grid>
            </Grid>
          )}
          {step?.currentRuleset && (
            <Grid item xs={12}>
              <Grid container direction="row" alignItems="flex-end" spaging="4">
                {!edit && (
                  <>
                    <Grid item xs={4}>
                      <Typography gutterBottom component="div">
                        <Box fontSize={14} mb={1} ml={1}>
                          {step?.currentRuleset?.rule_set?.name}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        size="medium"
                        color="primary"
                        disabled={loading}
                        startIcon={<EditIcon />}
                        data-cy="edit-pricing-rule"
                        onClick={() => setEdit(true)}
                        style={{ textTransform: 'capitalize' }}
                      >
                        Edit
                      </Button>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Typography gutterBottom component="div">
                    <Box mt={2} mb={1} fontSize={14} fontWeight="fontWeightMedium">
                      Effective since:{' '}
                      {moment(step?.currentRuleset.start_time).format(localeFormat)}
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          {step?.currentRuleset && (
            <Grid item xs={12}>
              <Typography gutterBottom component="div">
                <Box fontWeight="fontWeightMedium" fontSize={14} my={2}>
                  Scheduled Changes
                </Box>
              </Typography>
              <Typography gutterBottom component="div">
                <Box fontSize={14} mb={4} className={classes.textContent}>
                  You can schedule rule sets to apply for future dates. Once a change is scheduled,
                  all newly created reservations that start in that time period will inherit the
                  scheduled rule set.
                </Box>
              </Typography>
              <EditableTable
                addButtonProps={{
                  variant: 'outlined',
                  position: 'flex-end',
                  dataCy: 'add-new-rule',
                  label: i18n.t('pricing_rules.add_new_rule'),
                }}
                requiredFields={['rule_set_id']}
                data={parseTableRow() || []}
                columns={columns || []}
                onRowAdd={(values, resolve, reject) => {
                  let payload = {
                    rule_set_id: parseInt(values.rule_set_id),
                    rule_set_type: 'PRICING',
                  };

                  if (values.start_date) {
                    payload.start_date = moment(values.start_date).format('YYYY-MM-DD');
                  }

                  if (values.end_date) {
                    payload.end_date = moment(values.end_date).format('YYYY-MM-DD');
                  }
                  handleRuleset(payload, null, resolve, reject);
                }}
                onDelete={(value, resolve, reject) => {
                  deleteRuleset(
                    value.rule_set,
                    { data: { rule_set_type: 'PRICING' } },
                    resolve,
                    reject
                  );
                }}
                onEdit={(values, resolve, reject) => {
                  let payload = {
                    new_rule_set_id: parseInt(values.rule_set_id),
                    rule_set_type: 'PRICING',
                  };

                  if (values.start_date) {
                    payload.start_date = moment(values.start_date).format('YYYY-MM-DD');
                  }

                  if (values.end_date) {
                    payload.end_date = moment(values.end_date).format('YYYY-MM-DD');
                  }
                  handleRuleset(payload, values.rule_set, resolve, reject);
                }}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default VehiclesGroupsPricing;
